import * as React from 'react';
import { cn } from '@/shared/lib/cn';
import { useMaskedInput } from '@/shared/lib/useMaskedInput';
import { Input, type InputProps } from '../Input';
import { completeTime, maskTimeEdit, stepTime } from './time-mask';
import styles from './TimeInput.module.css';

export interface TimeInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: string;
  onChange: (value: string) => void;
  step?: number;
}

/**
 * Поле времени `ЧЧ:ММ` с маской.
 *
 * Двоеточие ставится само: набрал `9` — в поле `09:`, набрал `30` — `09:30`.
 * Стрелки вверх/вниз двигают время шагом `step` (с Shift — вчетверо крупнее),
 * уход из поля достраивает недобранное (`9` → `09:00`). Каретка при правке
 * часов не убегает в конец к минутам (см. useMaskedInput).
 *
 * Значение остаётся строкой, а не числом минут: расписание календаря хранится
 * и валидируется строками (`shared/lib/work-intervals`), и промежуточный ввод
 * не должен превращаться в «ноль минут».
 *
 * @use ввод времени `ЧЧ:ММ` с маской и стрелками.
 */
export const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(function TimeInput(
  { value, onChange, step = 15, className, onKeyDown, onBlur, ...rest },
  ref,
) {
  const masked = useMaskedInput({ value, mask: maskTimeEdit, onChange });
  React.useImperativeHandle(ref, () => masked.ref.current as HTMLInputElement, [masked.ref]);

  return (
    <Input
      ref={masked.ref}
      className={cn(styles.timeInput, className)}
      value={value}
      inputMode="numeric"
      autoComplete="off"
      placeholder="--:--"
      maxLength={5}
      onChange={masked.onChange}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          const delta = (e.key === 'ArrowUp' ? step : -step) * (e.shiftKey ? 4 : 1);
          onChange(stepTime(value, delta));
        }
        onKeyDown?.(e);
      }}
      onBlur={(e) => {
        const done = completeTime(value);
        if (done !== value) onChange(done);
        onBlur?.(e);
      }}
      {...rest}
    />
  );
});
